import type { SupportTicket, TicketSeverity } from './useSupportTickets';

/* ── Helpers puros da visão SupportTickets (testáveis, sem framework) ── */

export const TICKET_STATUS_META: Record<string, { variant: 'info' | 'warning' | 'success' | 'neutral'; label: string }> = {
  OPEN: { variant: 'info', label: 'Aberto' },
  IN_PROGRESS: { variant: 'warning', label: 'Em andamento' },
  WAITING_CUSTOMER: { variant: 'warning', label: 'Aguard. cliente' },
  WAITING_CLIENT: { variant: 'warning', label: 'Aguard. cliente' },
  RESOLVED: { variant: 'success', label: 'Resolvido' },
  CLOSED: { variant: 'neutral', label: 'Fechado' },
};

export function ticketStatusMeta(status: string): { variant: 'info' | 'warning' | 'success' | 'neutral'; label: string } {
  return TICKET_STATUS_META[status] ?? { variant: 'neutral', label: status };
}

export const SEVERITY_META: Record<TicketSeverity, { variant: 'error' | 'warning' | 'info' | 'neutral'; label: string }> = {
  CRITICAL: { variant: 'error', label: 'Crítica' },
  HIGH: { variant: 'warning', label: 'Alta' },
  MEDIUM: { variant: 'info', label: 'Média' },
  LOW: { variant: 'neutral', label: 'Baixa' },
};

export function ticketSeverityMeta(severity: string): { variant: 'error' | 'warning' | 'info' | 'neutral'; label: string } {
  return (SEVERITY_META as Record<string, { variant: 'error' | 'warning' | 'info' | 'neutral'; label: string }>)[severity] ?? {
    variant: 'neutral',
    label: severity,
  };
}

/** SLA contratual por severidade (backend sla-calculator / SEVERITY_SLA_HOURS). */
export const SLA_HOURS: Record<TicketSeverity, number> = {
  LOW: 72,
  MEDIUM: 24,
  HIGH: 8,
  CRITICAL: 2,
};

const TERMINAL_STATUSES = new Set(['RESOLVED', 'CLOSED']);

/**
 * Limite de SLA do ticket: usa `slaLimitAt` do servidor quando presente;
 * senão estima createdAt + SLA da severidade (retorna { limit, estimated }).
 */
export function ticketSlaLimit(ticket: SupportTicket): { limit: Date | null; estimated: boolean } {
  if (ticket.slaLimitAt) {
    const d = new Date(ticket.slaLimitAt);
    return { limit: Number.isNaN(d.getTime()) ? null : d, estimated: false };
  }
  const created = new Date(ticket.createdAt);
  if (Number.isNaN(created.getTime())) return { limit: null, estimated: true };
  const hours = (SLA_HOURS as Record<string, number>)[ticket.severity] ?? 24;
  return { limit: new Date(created.getTime() + hours * 3_600_000), estimated: true };
}

/** SLA estourado = não terminal e agora > limite. */
export function isSlaBreached(ticket: SupportTicket, now: Date = new Date()): boolean {
  if (TERMINAL_STATUSES.has(ticket.status)) return false;
  const { limit } = ticketSlaLimit(ticket);
  if (!limit) return false;
  return now.getTime() > limit.getTime();
}

export interface TicketsSummary {
  count: number;
  open: number;
  criticalHigh: number;
  slaBreached: number;
  csatAverage: number | null;
  csatCount: number;
  bySeverity: Record<TicketSeverity, number>;
}

/** Agregação client-side sobre os itens carregados (rotular "página atual" na UI). */
export function summarizeTickets(items: SupportTicket[], now: Date = new Date()): TicketsSummary {
  const bySeverity: Record<TicketSeverity, number> = { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
  let open = 0;
  let slaBreached = 0;
  let csatSum = 0;
  let csatCount = 0;
  for (const t of items) {
    if ((bySeverity as Record<string, number>)[t.severity] !== undefined) {
      bySeverity[t.severity] += 1;
    }
    if (!TERMINAL_STATUSES.has(t.status)) open += 1;
    if (isSlaBreached(t, now)) slaBreached += 1;
    if (typeof t.csatScore === 'number') {
      csatSum += t.csatScore;
      csatCount += 1;
    }
  }
  return {
    count: items.length,
    open,
    criticalHigh: bySeverity.CRITICAL + bySeverity.HIGH,
    slaBreached,
    csatAverage: csatCount > 0 ? csatSum / csatCount : null,
    csatCount,
    bySeverity,
  };
}

/** Busca client-side sobre número, assunto e descrição. */
export function filterTickets(items: SupportTicket[], search: string): SupportTicket[] {
  const term = search.trim().toLowerCase();
  if (!term) return items;
  return items.filter((t) =>
    [t.number, t.subject, t.description ?? ''].some((field) =>
      (field ?? '').toLowerCase().includes(term),
    ),
  );
}
